import { useState, useEffect, useRef } from "react";
import { api, API_URL } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  UploadCloud, Loader2, FileText, CheckCircle2, AlertTriangle,
  Lock, Unlock, RefreshCw, History, Eye, Trash2, Info,
} from "lucide-react";

/**
 * Historical Payroll Recovery — sub-tab inside Salary.jsx (Super Admin only).
 *
 * Flow: Upload PDF -> Preview (edit months, dedupe strategy) -> Confirm.
 * The confirmed snapshot writes:
 *   - historical_payroll_snapshots
 *   - historical_attendance_summary (monthly only, no invented dates)
 *   - advances (only when advance > 0)
 * The original PDF is stored PRIVATELY in GridFS.
 */
export default function HistoricalPayrollRecovery({ session }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [attendanceMonth, setAttendanceMonth] = useState("");
  const [paymentMonth, setPaymentMonth] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [center, setCenter] = useState("");
  const [dedupe, setDedupe] = useState("skip");
  const [documentType, setDocumentType] = useState("CENTRE_SALARY");
  const [rows, setRows] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [statusFilter, setStatusFilter] = useState("active"); // active | locked | editable | duplicates | deleted | all
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  // Delete dialog state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const reset = () => {
    setPreview(null);
    setRows([]);
    setAttendanceMonth(""); setPaymentMonth("");
    setInvoiceNumber(""); setInvoiceDate("");
    setCenter(""); setDedupe("skip");
    if (fileRef.current) fileRef.current.value = "";
  };

  const refreshList = async () => {
    if (!session?.token) return;
    setLoadingList(true);
    try {
      const includeDeleted = statusFilter === "deleted" || statusFilter === "all";
      const r = await api.get("/historical-payroll/list", {
        params: { token: session.token, include_deleted: includeDeleted },
      });
      setSnapshots(r.data?.snapshots || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load history");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { refreshList(); }, [session?.token, statusFilter]);

  const onPickFile = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload a PDF file.");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("token", session.token);
      form.append("file", file);
      form.append("document_type", documentType);
      const r = await api.post("/historical-payroll/upload/preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      setCenter(r.data.detected?.center || "");
      setAttendanceMonth(r.data.detected?.attendance_month || "");
      setPaymentMonth(r.data.detected?.payment_month || "");
      setInvoiceNumber(r.data.detected?.invoice_number || "");
      setInvoiceDate(r.data.detected?.invoice_date_iso || "");
      setRows(r.data.rows.map((row) => ({
        ...row,
        employee_id: row.match?.employee_id || null,
      })));
      // Auto-pick replace strategy when a fuzzy possible-duplicate exists.
      const hasBlocking = (r.data.duplicates || []).some((d) => d.severity === "blocking");
      const hasPossible = (r.data.duplicates || []).some((d) => d.severity === "possible");
      if (r.data.parse_error) {
        // Specific, actionable parser error — always surface it even when
        // the request itself succeeded.
        toast.error(r.data.parse_error, { duration: 12000 });
      } else if (hasBlocking) {
        toast.error("DUPLICATE HISTORICAL PAYROLL PDF — this exact file has already been imported.");
      } else if (hasPossible) {
        setDedupe("replace");
        toast.warning("Possible duplicate detected — pick a strategy (Skip / Replace) before confirming.");
      } else {
        toast.success(`Parsed ${r.data.totals.employee_count} employees (${r.data.source === "ocr" ? "OCR fallback" : "PDF text layer"})`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse PDF");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const totals = rows.reduce(
    (acc, r) => ({
      salary: acc.salary + (Number(r.monthly_salary) || 0),
      days: acc.days + (Number(r.no_of_days) || 0),
      advance: acc.advance + (Number(r.advance) || 0),
      pay: acc.pay + (Number(r.salary_to_pay) || 0),
    }),
    { salary: 0, days: 0, advance: 0, pay: 0 },
  );

  const confirmImport = async () => {
    if (!center || !attendanceMonth || !paymentMonth || !invoiceNumber) {
      toast.error("Centre, Attendance Month, Payment Month and Invoice Number are required.");
      return;
    }
    if (!rows.length) {
      toast.error("At least one employee row is required.");
      return;
    }
    setConfirming(true);
    try {
      const r = await api.post("/historical-payroll/confirm", {
        token: session.token,
        file_id: preview.file_id,
        center: center.toUpperCase(),
        attendance_month: attendanceMonth,
        payment_month: paymentMonth,
        invoice_number: invoiceNumber,
        invoice_date: invoiceDate || null,
        rows: rows.map((row) => ({
          employee_name: row.employee_name,
          designation: row.designation || "",
          center: (row.center || center).toUpperCase(),
          monthly_salary: Number(row.monthly_salary) || 0,
          no_of_days: Number(row.no_of_days) || 0,
          advance: Number(row.advance) || 0,
          salary_to_pay: Number(row.salary_to_pay) || 0,
          payment_amount: Number(row.payment_amount ?? row.salary_to_pay) || 0,
          employee_id: row.employee_id || null,
        })),
        dedupe_strategy: dedupe,
        document_type: documentType,
      });
      toast.success(`Historical payroll imported. Snapshot ${r.data.snapshot_id.slice(-6)} • ${r.data.attendance_rows} attendance rows • ${r.data.advances_created} advance txn(s).`);
      reset();
      refreshList();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && typeof d === "object") {
        if (d.code === "DUPLICATE_FILE") {
          toast.error(`DUPLICATE PDF — already imported as ${d.center} / ${d.attendance_month} / ${d.invoice_number}. Use Replace strategy if you truly need to overwrite.`);
        } else {
          toast.error(d.message || "Duplicate snapshot exists. Choose Replace strategy to overwrite.");
        }
      } else {
        toast.error(typeof d === "string" ? d : "Failed to import");
      }
    } finally {
      setConfirming(false);
    }
  };

  // ── Delete flow ─────────────────────────────────────────────────
  const openDelete = (snap) => {
    if (snap.locked) {
      toast.error("Locked snapshots must be unlocked first (with a reason) before deletion.");
      return;
    }
    setDeleteTarget(snap);
    setDeleteReason("");
    setDeleteConfirm(false);
  };

  const runDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const r = await api.post("/historical-payroll/delete", {
        token: session.token,
        snapshot_id: deleteTarget.id,
        reason: deleteReason.trim(),
        confirm_duplicate: deleteConfirm,
      });
      toast.success(`Snapshot deleted. Removed ${r.data.attendance_rows_removed} attendance rows and ${r.data.advances_removed} advance txn(s). PDF retained for audit.`);
      setDeleteTarget(null);
      refreshList();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (typeof d === "object") toast.error(d.message || "Delete failed");
      else toast.error(typeof d === "string" ? d : "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const openDetail = async (snap) => {
    try {
      const r = await api.get("/historical-payroll/detail", {
        params: { token: session.token, snapshot_id: snap.id },
      });
      setDetail(r.data);
      setDetailOpen(true);
    } catch {
      toast.error("Could not load snapshot detail");
    }
  };

  const lockSnap = async (snap) => {
    if (!window.confirm(`Lock this historical payroll (${snap.center} / ${snap.attendance_month})? It cannot be edited unless a Super Admin unlocks with a reason.`)) return;
    try {
      await api.post("/historical-payroll/lock", {
        token: session.token, snapshot_id: snap.id, reason: "Verified & locked",
      });
      toast.success("Snapshot locked");
      refreshList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lock failed");
    }
  };

  const unlockSnap = async (snap) => {
    const reason = window.prompt("Unlock reason (required):");
    if (!reason) return;
    try {
      await api.post("/historical-payroll/unlock", {
        token: session.token, snapshot_id: snap.id, reason,
      });
      toast.success("Snapshot unlocked");
      refreshList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unlock failed");
    }
  };

  const viewPdf = (snap) => {
    if (!snap?.file_id) { toast.error("No PDF on file"); return; }
    // Private, token-gated stream (never a public URL).
    const url = `${API_URL}/historical-payroll/pdf/${snap.file_id}?token=${encodeURIComponent(session.token)}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <Card className="mt-6" data-testid="historical-payroll-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="w-5 h-5" /> Historical Payroll Recovery
        </CardTitle>
        <CardDescription>
          Reconstruct old salary + attendance from monthly salary invoice PDFs when the original attendance workbook is lost.
          Parsing runs locally (PDF text layer → Tesseract OCR fallback). No external LLM is used. Original PDFs are stored privately.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Document type — CENTRE_SALARY (default) or MGT_ALLOCATION */}
        <div className="flex flex-wrap items-center gap-4 rounded-md border border-slate-200 bg-slate-50 p-3">
          <span className="text-sm font-semibold text-slate-700">Document Type:</span>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="hp-doctype-centre">
            <input type="radio" name="hp-doctype" value="CENTRE_SALARY"
              checked={documentType === "CENTRE_SALARY"}
              onChange={() => setDocumentType("CENTRE_SALARY")}
              disabled={!!preview} />
            <span>Centre Salary</span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="hp-doctype-mgt">
            <input type="radio" name="hp-doctype" value="MGT_ALLOCATION"
              checked={documentType === "MGT_ALLOCATION"}
              onChange={() => setDocumentType("MGT_ALLOCATION")}
              disabled={!!preview} />
            <span>MGT Allocation (already-divided PB-MGT share for this centre)</span>
          </label>
          {documentType === "MGT_ALLOCATION" && (
            <span className="text-[11px] text-amber-800 bg-amber-100 px-2 py-1 rounded">
              MGT PDFs are pre-divided — amounts will be recovered as-printed.  No attendance / advance rows are created.
            </span>
          )}
        </div>

        {/* Upload */}
        <div className="flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept="application/pdf" onChange={onFile} hidden data-testid="hp-file-input" />
          <Button onClick={onPickFile} disabled={uploading} data-testid="hp-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-2" />}
            Upload {documentType === "MGT_ALLOCATION" ? "MGT Allocation" : "Salary"} PDF
          </Button>
          {preview && (
            <>
              <Badge variant="outline" data-testid="hp-source-badge">
                {preview.source === "ocr" ? "OCR fallback" : "PDF text layer"}
              </Badge>
              <Button variant="ghost" size="sm" onClick={reset}>
                <RefreshCw className="w-4 h-4 mr-1" /> Reset
              </Button>
            </>
          )}
        </div>

        {/* Preview section */}
        {preview && (
          <div className="space-y-4">
            {preview.duplicates?.length > 0 && (
              <Alert variant="destructive" data-testid="hp-duplicate-alert">
                <AlertTriangle className="w-4 h-4" />
                <AlertTitle>
                  {preview.duplicates.some((d) => d.severity === "blocking")
                    ? "DUPLICATE HISTORICAL PAYROLL PDF"
                    : "Possible duplicate snapshot"}
                </AlertTitle>
                <AlertDescription>
                  {preview.duplicates.map((d, i) => (
                    <div key={i} className="mt-1">
                      {d.severity === "blocking" ? (
                        <>This file was already imported as
                        <strong> {d.center} / {d.attendance_month} / {d.invoice_number}</strong>
                        {d.locked ? " (LOCKED)" : ""}.
                        Confirm Import is disabled — <strong>Replace</strong> requires
                        an explicit choice below and a reason.</>
                      ) : (
                        <>A different PDF already exists for
                        <strong> {center} / {attendanceMonth} / {invoiceNumber}</strong>. Pick
                        &ldquo;Replace previous snapshot&rdquo; below or Cancel.</>
                      )}
                    </div>
                  ))}
                </AlertDescription>
              </Alert>
            )}

            {preview.warnings?.length > 0 && (
              <Alert data-testid="hp-warnings">
                <Info className="w-4 h-4" />
                <AlertTitle>Warnings</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc pl-5">
                    {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Metadata grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label>Centre</Label>
                <Input value={center} onChange={(e) => setCenter(e.target.value.toUpperCase())} data-testid="hp-center-input" />
              </div>
              <div>
                <Label>Attendance Month</Label>
                <Input type="month" value={attendanceMonth} onChange={(e) => setAttendanceMonth(e.target.value)} data-testid="hp-att-month" />
              </div>
              <div>
                <Label>Payment Month</Label>
                <Input type="month" value={paymentMonth} onChange={(e) => setPaymentMonth(e.target.value)} data-testid="hp-pay-month" />
              </div>
              <div>
                <Label>Invoice Number</Label>
                <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} data-testid="hp-invoice-num" />
              </div>
              <div>
                <Label>Invoice Date</Label>
                <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} data-testid="hp-invoice-date" />
              </div>
              <div>
                <Label>If duplicate exists</Label>
                <select className="w-full h-10 border rounded-md px-2 text-sm" value={dedupe} onChange={(e) => setDedupe(e.target.value)} data-testid="hp-dedupe">
                  <option value="skip">Skip (fail if duplicate)</option>
                  <option value="replace">Replace previous snapshot</option>
                </select>
              </div>
            </div>

            {/* Rows table */}
            <ScrollArea className="max-h-[420px] border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead>Match</TableHead>
                    <TableHead className="text-right">Monthly ₹</TableHead>
                    <TableHead className="text-right">Days</TableHead>
                    <TableHead className="text-right">Advance</TableHead>
                    <TableHead className="text-right">Salary Payable</TableHead>
                    <TableHead className="text-right">Payment</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, i) => {
                    const m = row.match || {};
                    const matched = m.matched;
                    return (
                      <TableRow key={i} data-testid={`hp-row-${i}`}>
                        <TableCell>
                          <Input value={row.employee_name} onChange={(e) => updateRow(i, { employee_name: e.target.value.toUpperCase() })} />
                        </TableCell>
                        <TableCell>
                          <Input value={row.designation} onChange={(e) => updateRow(i, { designation: e.target.value.toUpperCase() })} />
                        </TableCell>
                        <TableCell>
                          {matched ? (
                            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" title={m.employee_name}>
                              <CheckCircle2 className="w-3 h-3 mr-1" /> {m.employee_center || "matched"}
                            </Badge>
                          ) : m.candidates?.length ? (
                            <select
                              className="border rounded p-1 text-xs"
                              value={row.employee_id || ""}
                              onChange={(e) => updateRow(i, { employee_id: e.target.value })}
                            >
                              <option value="">Pick match…</option>
                              {m.candidates.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name} ({c.center})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <Badge variant="outline" className="text-amber-700">Manual review</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Input type="number" className="w-24 text-right" value={row.monthly_salary}
                            onChange={(e) => updateRow(i, { monthly_salary: e.target.value })} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Input type="number" className="w-16 text-right" value={row.no_of_days}
                            onChange={(e) => updateRow(i, { no_of_days: e.target.value })} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Input type="number" className="w-20 text-right" value={row.advance}
                            onChange={(e) => updateRow(i, { advance: e.target.value })} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Input type="number" className="w-24 text-right" value={row.salary_to_pay}
                            onChange={(e) => updateRow(i, { salary_to_pay: e.target.value })} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Input type="number" className="w-24 text-right" value={row.payment_amount ?? row.salary_to_pay}
                            onChange={(e) => updateRow(i, { payment_amount: e.target.value })} />
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => removeRow(i)} title="Drop row">
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>

            {/* Totals + Confirm */}
            <div className="flex flex-wrap justify-between items-center gap-3 border-t pt-4">
              <div className="text-sm text-muted-foreground">
                <div><strong>{rows.length}</strong> employees</div>
                <div>Total Salary: <strong>₹{totals.salary.toLocaleString("en-IN")}</strong></div>
                <div>Total Days: <strong>{totals.days}</strong></div>
                <div>Total Advance: <strong>₹{totals.advance.toLocaleString("en-IN")}</strong></div>
                <div>Total Salary Payable: <strong>₹{totals.pay.toLocaleString("en-IN")}</strong></div>
              </div>
              <Button size="lg" onClick={confirmImport}
                disabled={confirming || rows.length === 0 ||
                  ((preview?.duplicates || []).some((d) => d.severity === "blocking") && dedupe !== "replace")}
                data-testid="hp-confirm-btn">
                {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Confirm Import
              </Button>
            </div>

            {rows.length === 0 && (
              <Alert variant="destructive" data-testid="hp-zero-rows-alert">
                <AlertTriangle className="w-4 h-4" />
                <AlertTitle>
                  {preview.parse_error
                    ? "Parser could not extract rows"
                    : "Text extracted, but no employee rows could be structured"}
                </AlertTitle>
                <AlertDescription>
                  {preview.parse_error ? (
                    <span data-testid="hp-parse-error-msg">{preview.parse_error}</span>
                  ) : (
                    <>
                      The PDF text layer was read successfully, but the row parser
                      couldn&apos;t identify any employee rows.
                    </>
                  )}
                  <div className="mt-1 text-[11px]">
                    Expand <strong>Parsed Row Debug</strong> below to see which lines
                    were dropped and why. <strong>Confirm Import</strong> is disabled
                    until at least one row parses.
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {preview.parse_debug && preview.parse_debug.length > 0 && (
              <details className="text-xs" data-testid="hp-debug">
                <summary className="cursor-pointer text-muted-foreground">
                  Parsed Row Debug ({preview.parse_debug.filter(d => d.status === "parsed").length} parsed
                  / {preview.parse_debug.filter(d => d.status && d.status.startsWith("failed")).length} failed
                  / {preview.parse_debug.filter(d => d.status && d.status.startsWith("skipped")).length} skipped)
                </summary>
                <div className="mt-1 max-h-72 overflow-auto border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="p-1 text-left">Status</th>
                        <th className="p-1 text-left">Raw / Normalised</th>
                        <th className="p-1 text-left">Detected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.parse_debug.map((d, i) => (
                        <tr key={i} className={
                          d.status === "parsed" ? "bg-emerald-50" :
                          (d.status || "").startsWith("failed") ? "bg-red-50" :
                          "bg-white"
                        }>
                          <td className="p-1 align-top font-mono">{d.status}</td>
                          <td className="p-1 align-top">
                            <div className="text-muted-foreground">{d.raw}</div>
                            {d.normalised && d.normalised !== d.raw && (
                              <div className="text-[10px] text-blue-700">→ {d.normalised}</div>
                            )}
                          </td>
                          <td className="p-1 align-top">
                            {d.status === "parsed" ? (
                              <span>{d.name} • {d.designation} • ₹{d.salary} • {d.days}d • adv ₹{d.advance} • pay ₹{d.salary_to_pay}</span>
                            ) : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {preview.raw_text_preview && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">Show raw extracted text</summary>
                <pre className="whitespace-pre-wrap bg-muted p-2 rounded mt-1 max-h-64 overflow-auto">{preview.raw_text_preview}</pre>
              </details>
            )}
          </div>
        )}

        {/* Existing recovered snapshots */}
        <div className="pt-4 border-t">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h4 className="font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4" /> Recovered Historical Snapshots
            </h4>
            <div className="flex items-center gap-2">
              <select className="h-9 border rounded-md px-2 text-sm" value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)} data-testid="hp-filter">
                <option value="active">Active (locked + editable)</option>
                <option value="locked">Locked only</option>
                <option value="editable">Editable only</option>
                <option value="duplicates">Possible Duplicates</option>
                <option value="deleted">Deleted / Replaced</option>
                <option value="all">All</option>
              </select>
              <Button variant="ghost" size="sm" onClick={refreshList} disabled={loadingList}>
                {loadingList ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          {(() => {
            // Client-side filtering on top of server filter.
            const dupeKey = (s) => `${s.center}|${s.attendance_month}|${s.invoice_number}`;
            const counts = snapshots.reduce((acc, s) => {
              const k = dupeKey(s); acc[k] = (acc[k] || 0) + 1; return acc;
            }, {});
            let visible = snapshots;
            if (statusFilter === "locked") visible = visible.filter((s) => s.locked);
            else if (statusFilter === "editable") visible = visible.filter((s) => !s.locked && !s.deleted);
            else if (statusFilter === "duplicates") visible = visible.filter((s) => counts[dupeKey(s)] > 1);
            else if (statusFilter === "deleted") visible = visible.filter((s) => s.deleted);
            else if (statusFilter === "active") visible = visible.filter((s) => !s.deleted);
            if (visible.length === 0) return <p className="text-sm text-muted-foreground">No snapshots match this filter.</p>;
            return (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Centre</TableHead>
                    <TableHead>Attendance</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Invoice</TableHead>
                    <TableHead className="text-right">Employees</TableHead>
                    <TableHead className="text-right">Payable</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.map((s) => {
                    const isDup = counts[dupeKey(s)] > 1;
                    return (
                      <TableRow key={s.id} data-testid={`hp-snapshot-${s.id}`}
                        className={s.deleted ? "opacity-60 line-through" : (isDup ? "bg-amber-50/40" : "")}>
                        <TableCell><Badge variant="outline">{s.center}</Badge></TableCell>
                        <TableCell>{s.attendance_month}</TableCell>
                        <TableCell>{s.payment_month}</TableCell>
                        <TableCell className="text-xs">{s.invoice_number}<br/><span className="text-muted-foreground">{s.invoice_date}</span></TableCell>
                        <TableCell className="text-right">{s.totals?.employee_count}</TableCell>
                        <TableCell className="text-right">₹{(s.totals?.salary_to_pay || 0).toLocaleString("en-IN")}</TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            {s.deleted
                              ? <Badge variant="outline" className="text-destructive">Deleted</Badge>
                              : s.locked
                                ? <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"><Lock className="w-3 h-3 mr-1" /> Locked</Badge>
                                : <Badge variant="outline">Editable</Badge>}
                            {isDup && !s.deleted && (
                              <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100" title="Multiple snapshots share this Centre + Month + Invoice">Duplicate?</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="ghost" onClick={() => openDetail(s)} title="View rows" disabled={s.deleted}><Eye className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => viewPdf(s)} title="Open original PDF"><FileText className="w-4 h-4" /></Button>
                            {!s.deleted && (s.locked
                              ? <Button size="sm" variant="ghost" onClick={() => unlockSnap(s)} title="Unlock (reason required)"><Unlock className="w-4 h-4" /></Button>
                              : <Button size="sm" variant="ghost" onClick={() => lockSnap(s)} title="Lock snapshot"><Lock className="w-4 h-4" /></Button>)}
                            {!s.deleted && (
                              <Button size="sm" variant="ghost"
                                onClick={() => openDelete(s)}
                                disabled={s.locked}
                                title={s.locked ? "Unlock first, then delete" : "Delete this snapshot"}
                                data-testid={`hp-delete-${s.id}`}>
                                <Trash2 className={`w-4 h-4 ${s.locked ? "" : "text-destructive"}`} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            );
          })()}
        </div>
      </CardContent>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{detail?.center} — {detail?.attendance_month}</DialogTitle>
            <DialogDescription>
              Invoice {detail?.invoice_number} • Payment {detail?.payment_month} •
              Source: {detail?.source}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Designation</TableHead>
                  <TableHead className="text-right">Salary</TableHead>
                  <TableHead className="text-right">Cal Days</TableHead>
                  <TableHead className="text-right">Paid Days</TableHead>
                  <TableHead className="text-right">Absence</TableHead>
                  <TableHead className="text-right">Advance</TableHead>
                  <TableHead className="text-right">Payable</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(detail?.rows || []).map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.employee_name}</TableCell>
                    <TableCell>{r.designation}</TableCell>
                    <TableCell className="text-right">₹{(r.monthly_salary || 0).toLocaleString("en-IN")}</TableCell>
                    <TableCell className="text-right">{r.calendar_days}</TableCell>
                    <TableCell className="text-right">{r.paid_days}</TableCell>
                    <TableCell className="text-right">{r.absence_equivalent}</TableCell>
                    <TableCell className="text-right">₹{(r.advance || 0).toLocaleString("en-IN")}</TableCell>
                    <TableCell className="text-right">₹{(r.salary_to_pay || 0).toLocaleString("en-IN")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDetailOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="hp-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Delete Historical Payroll Snapshot?
            </DialogTitle>
            <DialogDescription>
              This action is audited. The original PDF is retained for audit
              &mdash; only the imported records are removed.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-y-1">
                <div className="text-muted-foreground">Centre</div>
                <div className="font-medium">{deleteTarget.center}</div>
                <div className="text-muted-foreground">Attendance Month</div>
                <div className="font-medium">{deleteTarget.attendance_month}</div>
                <div className="text-muted-foreground">Payment Month</div>
                <div className="font-medium">{deleteTarget.payment_month}</div>
                <div className="text-muted-foreground">Invoice</div>
                <div className="font-medium">{deleteTarget.invoice_number}</div>
                <div className="text-muted-foreground">Employees</div>
                <div className="font-medium">{deleteTarget.totals?.employee_count}</div>
                <div className="text-muted-foreground">Payable</div>
                <div className="font-medium">₹{(deleteTarget.totals?.salary_to_pay || 0).toLocaleString("en-IN")}</div>
              </div>
              <Alert variant="destructive">
                <AlertTriangle className="w-4 h-4" />
                <AlertTitle>Warning</AlertTitle>
                <AlertDescription>
                  Deleting this snapshot will remove the recovered payroll +
                  attendance summary + advance transactions tied to THIS import
                  only (by snapshot ID). Other snapshots are safe.
                </AlertDescription>
              </Alert>
              <div>
                <Label htmlFor="del-reason">Reason for deletion <span className="text-destructive">*</span></Label>
                <Textarea id="del-reason" value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="e.g. Duplicate PDF uploaded accidentally."
                  data-testid="hp-delete-reason" />
              </div>
              <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={deleteConfirm} onCheckedChange={setDeleteConfirm} data-testid="hp-delete-confirm-check" />
                <span>I confirm this is a duplicate or incorrect historical import.</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={runDelete}
              disabled={deleting || !deleteReason.trim() || !deleteConfirm}
              data-testid="hp-delete-submit">
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              DELETE SNAPSHOT
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
